import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { 
  Gift, 
  Settings, 
  Users, 
  TrendingUp, 
  Award, 
  Search,
  Plus,
  Edit2,
  History,
  Star,
  Crown,
  Medal,
  Phone,
  Mail,
  RotateCcw
} from 'lucide-react';
import axios from 'axios';

import { useAuth, API } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { getErrorMessage } from '../../utils/errorHandler';
import { formatAmount } from '../../utils/format';
const LoyaltyTab = () => {
  const { getAuthHeaders } = useAuth();
  const [activeSection, setActiveSection] = useState('config');
  const [loading, setLoading] = useState(false);
  
  // Config state
  const [config, setConfig] = useState({
    points_per_currency: 1,
    currency_per_point: 100,
    redemption_value: 0.05,
    min_points_redemption: 100,
    is_active: true,
    welcome_bonus: 50,
    birthday_bonus: 100
  });
  
  // Customers state
  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCustomerDialog, setShowCustomerDialog] = useState(false);
  const [showAdjustDialog, setShowAdjustDialog] = useState(false);
  const [showNewCustomerDialog, setShowNewCustomerDialog] = useState(false);
  const [showEditCustomerDialog, setShowEditCustomerDialog] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [customerToReset, setCustomerToReset] = useState(null);
  const [resetReason, setResetReason] = useState('');
  
  // Edit customer form
  const [editCustomerData, setEditCustomerData] = useState({ name: '', email: '', birthday: '' });
  
  // Stats state
  const [stats, setStats] = useState(null);
  
  // Form states
  const [adjustPoints, setAdjustPoints] = useState({ points: 0, reason: '' });
  const [newCustomer, setNewCustomer] = useState({ phone: '', name: '', email: '', birthday: '' });

  const loadConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/loyalty/config`, {
        headers: getAuthHeaders()
      });
      setConfig(response.data);
    } catch (error) {
      console.error('Error loading config:', error);
    }
  }, [getAuthHeaders]);

  const loadStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/loyalty/stats`, {
        headers: getAuthHeaders()
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }, [getAuthHeaders]);

  const loadCustomers = useCallback(async (search = '') => {
    try {
      const url = search 
        ? `${API}/loyalty/customers?search=${encodeURIComponent(search)}`
        : `${API}/loyalty/customers`;
      const response = await axios.get(url, {
        headers: getAuthHeaders()
      });
      setCustomers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    loadConfig();
    loadStats();
    loadCustomers();
  }, [loadConfig, loadStats, loadCustomers]);

  const saveConfig = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/loyalty/config`, config, {
        headers: getAuthHeaders()
      });
      toast.success('Configuración guardada exitosamente');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al guardar configuración'));
    }
    setLoading(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadCustomers(searchQuery);
  };

  const viewCustomerDetails = async (phone) => {
    try {
      const response = await axios.get(`${API}/loyalty/customers/${phone}`, {
        headers: getAuthHeaders()
      });
      setSelectedCustomer(response.data);
      setShowCustomerDialog(true);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al cargar cliente'));
    }
  };

  // El crédito se gestiona desde la ficha del usuario (UserManagementTab),
  // no desde fidelización: aquí solo se muestra el saldo en Cuentas por Cobrar.

  const handleAdjustPoints = async () => {
    if (!adjustPoints.points || !adjustPoints.reason) {
      toast.error('Ingresa puntos y razón del ajuste');
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('customer_phone', selectedCustomer.customer_phone);
      formData.append('points', adjustPoints.points);
      formData.append('reason', adjustPoints.reason);
      
      await axios.post(`${API}/loyalty/adjust`, formData, {
        headers: getAuthHeaders()
      });
      
      toast.success('Puntos ajustados exitosamente');
      setShowAdjustDialog(false);
      setAdjustPoints({ points: 0, reason: '' });
      viewCustomerDetails(selectedCustomer.customer_phone);
      loadCustomers(searchQuery);
      loadStats();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al ajustar puntos'));
    }
    setLoading(false);
  };

  const handleCreateCustomer = async () => {
    if (!newCustomer.phone) {
      toast.error('El teléfono es requerido');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/loyalty/customers`, {
        customer_phone: newCustomer.phone,
        customer_name: newCustomer.name || null,
        customer_email: newCustomer.email || null,
        birthday: newCustomer.birthday || null
      }, {
        headers: getAuthHeaders()
      });
      
      toast.success('Cliente registrado en programa de lealtad');
      setShowNewCustomerDialog(false);
      setNewCustomer({ phone: '', name: '', email: '', birthday: '' });
      loadCustomers();
      loadStats();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al registrar cliente'));
    }
    setLoading(false);
  };

  const handleResetPoints = async () => {
    if (!resetReason.trim()) {
      toast.error('Por favor ingresa la razón del reset');
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('customer_phone', customerToReset.customer_phone);
      formData.append('reason', resetReason);
      
      const response = await axios.post(`${API}/loyalty/reset`, formData, {
        headers: getAuthHeaders()
      });
      
      toast.success(response.data.message || 'Puntos reseteados exitosamente');
      setShowResetDialog(false);
      setCustomerToReset(null);
      setResetReason('');
      loadCustomers(searchQuery);
      loadStats();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al resetear puntos'));
    }
    setLoading(false);
  };

  const handleEditCustomer = async () => {
    if (!editCustomerData.name && !editCustomerData.email && !editCustomerData.birthday) {
      toast.error('Debes modificar al menos un campo');
      return;
    }
    
    setLoading(true);
    try {
      const updateData = {
        customer_phone: selectedCustomer.customer_phone
      };
      
      if (editCustomerData.name) updateData.customer_name = editCustomerData.name;
      if (editCustomerData.email) updateData.customer_email = editCustomerData.email;
      if (editCustomerData.birthday) updateData.birthday = editCustomerData.birthday;
      
      await axios.put(`${API}/loyalty/customers/${selectedCustomer.customer_phone}`, updateData, {
        headers: getAuthHeaders()
      });
      
      toast.success('Cliente actualizado exitosamente');
      setShowEditCustomerDialog(false);
      viewCustomerDetails(selectedCustomer.customer_phone);
      loadCustomers(searchQuery);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al actualizar cliente'));
    }
    setLoading(false);
  };

  const handleFixConfig = async () => {
    if (!window.confirm('¿Actualizar configuración a 1 punto por cada $100 gastados?')) {
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/loyalty/fix-config`, {}, {
        headers: getAuthHeaders()
      });
      
      toast.success(response.data.message || 'Configuración actualizada correctamente');
      loadConfig(); // Reload config to show updated values
      loadStats();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al actualizar configuración'));
    }
    setLoading(false);
  };

  const getTierIcon = (tier) => {
    switch (tier) {
      case 'platinum': return <Crown className="w-4 h-4 text-purple-500" />;
      case 'gold': return <Star className="w-4 h-4 text-yellow-500" />;
      case 'silver': return <Medal className="w-4 h-4 text-gray-400" />;
      default: return <Award className="w-4 h-4 text-orange-400" />;
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'platinum': return 'bg-purple-100 text-purple-700';
      case 'gold': return 'bg-yellow-100 text-yellow-700';
      case 'silver': return 'bg-gray-100 text-gray-700';
      default: return 'bg-orange-100 text-orange-700';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
            <Gift className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Programa de Fidelidad</h2>
            <p className="text-gray-500">Gestiona puntos y recompensas para tus clientes</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 pb-2">
        <Button
          variant={activeSection === 'config' ? 'default' : 'ghost'}
          onClick={() => setActiveSection('config')}
          className={activeSection === 'config' ? 'bg-purple-500 hover:bg-purple-600' : ''}
        >
          <Settings className="w-4 h-4 mr-2" />
          Configuración
        </Button>
        <Button
          variant={activeSection === 'customers' ? 'default' : 'ghost'}
          onClick={() => setActiveSection('customers')}
          className={activeSection === 'customers' ? 'bg-purple-500 hover:bg-purple-600' : ''}
        >
          <Users className="w-4 h-4 mr-2" />
          Clientes
        </Button>
        <Button
          variant={activeSection === 'stats' ? 'default' : 'ghost'}
          onClick={() => setActiveSection('stats')}
          className={activeSection === 'stats' ? 'bg-purple-500 hover:bg-purple-600' : ''}
        >
          <TrendingUp className="w-4 h-4 mr-2" />
          Estadísticas
        </Button>
      </div>

      {/* Configuration Section */}
      {activeSection === 'config' && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold">Configuración del Programa</h3>
            <div className="flex items-center gap-2">
              <Label>Programa Activo</Label>
              <Switch
                checked={config.is_active}
                onCheckedChange={(checked) => setConfig({ ...config, is_active: checked })}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Points Earning */}
            <div className="space-y-4 p-4 bg-purple-50 rounded-xl">
              <h4 className="font-medium text-purple-700 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Acumulación de Puntos
              </h4>
              
              <div>
                <Label>Dinero por cada punto ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={config.currency_per_point}
                  onChange={(e) => setConfig({ ...config, currency_per_point: parseFloat(e.target.value) || 100 })}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Ejemplo: Si pones 100, el cliente gana 1 punto por cada $100 gastados
                </p>
              </div>

              <div>
                <Label>Puntos por unidad de dinero</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0.1"
                  value={config.points_per_currency}
                  onChange={(e) => setConfig({ ...config, points_per_currency: parseFloat(e.target.value) || 1 })}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Multiplicador de puntos (1 = normal, 2 = doble puntos)
                </p>
              </div>
            </div>

            {/* Points Redemption */}
            <div className="space-y-4 p-4 bg-pink-50 rounded-xl">
              <h4 className="font-medium text-pink-700 flex items-center gap-2">
                <Gift className="w-4 h-4" />
                Canje de Puntos
              </h4>
              
              <div>
                <Label>Valor de cada punto al canjear ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={config.redemption_value}
                  onChange={(e) => setConfig({ ...config, redemption_value: parseFloat(e.target.value) || 0.05 })}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Ejemplo: Si pones 0.05, 100 puntos = $5.00 de descuento
                </p>
              </div>

              <div>
                <Label>Mínimo de puntos para canjear</Label>
                <Input
                  type="number"
                  min="1"
                  value={config.min_points_redemption}
                  onChange={(e) => setConfig({ ...config, min_points_redemption: parseInt(e.target.value) || 100 })}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Bonuses */}
            <div className="space-y-4 p-4 bg-green-50 rounded-xl md:col-span-2">
              <h4 className="font-medium text-green-700 flex items-center gap-2">
                <Award className="w-4 h-4" />
                Bonificaciones
              </h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Bono de Bienvenida (puntos)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={config.welcome_bonus}
                    onChange={(e) => setConfig({ ...config, welcome_bonus: parseInt(e.target.value) || 0 })}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Puntos al registrarse
                  </p>
                </div>

                <div>
                  <Label>Bono de Cumpleaños (puntos)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={config.birthday_bonus}
                    onChange={(e) => setConfig({ ...config, birthday_bonus: parseInt(e.target.value) || 0 })}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Puntos extra en cumpleaños
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="mt-6 p-4 bg-gray-50 rounded-xl">
            <h4 className="font-medium mb-2">Vista Previa del Programa</h4>
            <div className="text-sm text-gray-600 space-y-1">
              <p>• Por cada <strong>${formatAmount(config.currency_per_point || 1)}</strong> gastado, el cliente gana <strong>{config.points_per_currency || 1}</strong> punto(s)</p>
              <p>• <strong>{config.min_points_redemption || 100}</strong> puntos = <strong>${formatAmount((config.min_points_redemption || 100) * (config.redemption_value || 0.05))}</strong> de descuento</p>
              <p>• Bono de bienvenida: <strong>{config.welcome_bonus || 0}</strong> puntos</p>
            </div>
          </div>

          <div className="mt-6 flex justify-between items-center">
            <Button 
              onClick={handleFixConfig} 
              disabled={loading}
              variant="outline"
              className="border-orange-500 text-orange-600 hover:bg-orange-50"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Arreglar Cálculo (1pt/$100)
            </Button>
            <Button 
              onClick={saveConfig} 
              disabled={loading}
              className="bg-purple-500 hover:bg-purple-600"
            >
              {loading ? 'Guardando...' : 'Guardar Configuración'}
            </Button>
          </div>
        </Card>
      )}

      {/* Customers Section */}
      {activeSection === 'customers' && (
        <div className="space-y-4">
          {/* Search and Add */}
          <div className="flex gap-4">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Buscar por teléfono o nombre..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button type="submit" variant="outline">Buscar</Button>
            </form>
            <Button 
              onClick={() => setShowNewCustomerDialog(true)}
              className="bg-purple-500 hover:bg-purple-600"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Cliente
            </Button>
          </div>

          {/* Customers List */}
          <Card className="divide-y">
            {customers.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No hay clientes registrados en el programa</p>
              </div>
            ) : (
              customers.map((customer) => (
                <div 
                  key={customer.id} 
                  className="p-4 flex items-center justify-between hover:bg-gray-50"
                >
                  <div 
                    className="flex items-center gap-4 flex-1 cursor-pointer"
                    onClick={() => viewCustomerDetails(customer.customer_phone)}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getTierColor(customer.tier)}`}>
                      {getTierIcon(customer.tier)}
                    </div>
                    <div>
                      <p className="font-medium">{customer.customer_name || customer.customer_phone}</p>
                      <p className="text-sm text-gray-500">{customer.customer_phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="font-bold text-purple-600">{customer.total_points || 0} pts</p>
                      {/* El badge de tier se pinta solo si hay tier: sin él, la concatenación
                          de undefined producía un literal "NaN" en cada fila. */}
                      {customer.tier && (
                        <Badge className={getTierColor(customer.tier)}>
                          {customer.tier.charAt(0).toUpperCase() + customer.tier.slice(1)}
                        </Badge>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      title="Resetear puntos"
                      aria-label="Resetear puntos"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCustomerToReset(customer);
                        setShowResetDialog(true);
                      }}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </Card>
        </div>
      )}

      {/* Stats Section */}
      {activeSection === 'stats' && stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card key="total-customers" className="p-4 bg-gradient-to-br from-purple-50 to-purple-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Clientes</p>
                <p className="text-2xl font-bold text-purple-700">{stats.total_customers}</p>
              </div>
            </div>
          </Card>

          <Card key="points-outstanding" className="p-4 bg-gradient-to-br from-pink-50 to-pink-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-pink-500 rounded-lg flex items-center justify-center">
                <Award className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Puntos en Circulación</p>
                <p className="text-2xl font-bold text-pink-700">{stats.total_points_outstanding?.toLocaleString()}</p>
              </div>
            </div>
          </Card>

          <Card key="points-redeemed" className="p-4 bg-gradient-to-br from-green-50 to-green-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                <Gift className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Puntos Canjeados</p>
                <p className="text-2xl font-bold text-green-700">{stats.total_points_redeemed?.toLocaleString()}</p>
              </div>
            </div>
          </Card>

          <Card key="revenue" className="p-4 bg-gradient-to-br from-orange-50 to-orange-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Ingresos de Miembros</p>
                <p className="text-2xl font-bold text-orange-700">${formatAmount(stats.total_revenue_from_members)}</p>
              </div>
            </div>
          </Card>

          {/* Tier Distribution */}
          <Card key="tier-distribution" className="p-4 md:col-span-2">
            <h4 className="font-medium mb-4">Distribución por Nivel</h4>
            <div className="grid grid-cols-4 gap-2">
              {['bronze', 'silver', 'gold', 'platinum'].map((tier) => (
                <div key={tier} className={`p-3 rounded-lg text-center ${getTierColor(tier)}`}>
                  <div className="flex justify-center mb-1">{getTierIcon(tier)}</div>
                  <p className="text-xs capitalize">{tier}</p>
                  <p className="font-bold">{stats.tier_distribution?.[tier] || 0}</p>
                </div>
              ))}
            </div>
          </Card>

          <Card key="recent-activity" className="p-4 md:col-span-2">
            <h4 className="font-medium mb-4">Actividad Reciente</h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Transacciones (30 días)</span>
                <span className="font-bold">{stats.transactions_last_30_days || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Promedio puntos/cliente</span>
                <span className="font-bold">{stats.avg_points_per_customer || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Puntos totales otorgados</span>
                <span className="font-bold">{stats.total_points_lifetime?.toLocaleString() || 0}</span>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Customer Details Dialog */}
      <Dialog open={showCustomerDialog} onOpenChange={setShowCustomerDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getTierColor(selectedCustomer?.tier)}`}>
                {getTierIcon(selectedCustomer?.tier)}
              </div>
              {selectedCustomer?.customer_name || 'Cliente'}
            </DialogTitle>
            <DialogDescription>
              Detalles del cliente, historial de puntos y transacciones recientes
            </DialogDescription>
          </DialogHeader>
          
          {selectedCustomer && (
            <div className="space-y-4">
              {/* Customer Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2 text-gray-600">
                  <Phone className="w-4 h-4" />
                  {selectedCustomer.customer_phone}
                </div>
                {selectedCustomer.customer_email && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Mail className="w-4 h-4" />
                    {selectedCustomer.customer_email}
                  </div>
                )}
              </div>

              {/* Points Summary */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-purple-50 rounded-xl">
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-600">{selectedCustomer.total_points || 0}</p>
                  <p className="text-xs text-gray-500">Puntos Actuales</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{selectedCustomer.lifetime_points || 0}</p>
                  <p className="text-xs text-gray-500">Total Ganados</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-pink-600">{selectedCustomer.redeemed_points || 0}</p>
                  <p className="text-xs text-gray-500">Canjeados</p>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-gray-500">Total Gastado</p>
                  <p className="font-bold">${formatAmount(selectedCustomer.total_spent)}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-gray-500">Visitas</p>
                  <p className="font-bold">{selectedCustomer.visit_count || 0}</p>
                </div>
              </div>

              {/* Recent Transactions */}
              {selectedCustomer.recent_transactions?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <History className="w-4 h-4" />
                    Transacciones Recientes
                  </h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selectedCustomer.recent_transactions.map((tx, idx) => (
                      <div key={idx} className="flex justify-between text-sm p-2 bg-gray-50 rounded">
                        <span className="text-gray-600">{tx.description}</span>
                        <span className={tx.points > 0 ? 'text-green-600' : 'text-red-600'}>
                          {tx.points > 0 ? '+' : ''}{tx.points} pts
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setEditCustomerData({
                      name: selectedCustomer.customer_name || '',
                      email: selectedCustomer.customer_email || '',
                      birthday: selectedCustomer.birthday || ''
                    });
                    setShowEditCustomerDialog(true);
                  }}
                >
                  <Edit2 className="w-4 h-4 mr-2" />
                  Editar Datos
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowAdjustDialog(true);
                  }}
                >
                  <Award className="w-4 h-4 mr-2" />
                  Ajustar Puntos
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Adjust Points Dialog */}
      <Dialog open={showAdjustDialog} onOpenChange={setShowAdjustDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajustar Puntos</DialogTitle>
            <DialogDescription>
              Agrega o resta puntos manualmente al cliente seleccionado
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Puntos (negativo para restar)</Label>
              <Input
                type="number"
                value={adjustPoints.points}
                onChange={(e) => setAdjustPoints({ ...adjustPoints, points: parseInt(e.target.value) || 0 })}
                placeholder="Ej: 50 o -20"
              />
            </div>
            <div>
              <Label>Razón del ajuste</Label>
              <Input
                value={adjustPoints.reason}
                onChange={(e) => setAdjustPoints({ ...adjustPoints, reason: e.target.value })}
                placeholder="Ej: Compensación por error, Promoción especial..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdjustDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleAdjustPoints} 
              disabled={loading}
              className="bg-purple-500 hover:bg-purple-600"
            >
              {loading ? 'Aplicando...' : 'Aplicar Ajuste'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Customer Dialog */}
      <Dialog open={showNewCustomerDialog} onOpenChange={setShowNewCustomerDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar Cliente en Programa de Lealtad</DialogTitle>
            <DialogDescription>
              Inscribe a un nuevo cliente en el programa de puntos y recompensas
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Teléfono *</Label>
              <Input
                value={newCustomer.phone}
                onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                placeholder="Ej: 8091234567"
              />
            </div>
            <div>
              <Label>Nombre</Label>
              <Input
                value={newCustomer.name}
                onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                placeholder="Nombre del cliente"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={newCustomer.email}
                onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                placeholder="email@ejemplo.com"
              />
            </div>
            <div>
              <Label>Cumpleaños (MM-DD)</Label>
              <Input
                value={newCustomer.birthday}
                onChange={(e) => setNewCustomer({ ...newCustomer, birthday: e.target.value })}
                placeholder="Ej: 03-15 (15 de marzo)"
              />
            </div>
            
            {config.welcome_bonus > 0 && (
              <div className="p-3 bg-green-50 rounded-lg text-sm text-green-700">
                🎁 El cliente recibirá <strong>{config.welcome_bonus} puntos</strong> de bienvenida
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewCustomerDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCreateCustomer} 
              disabled={loading}
              className="bg-purple-500 hover:bg-purple-600"
            >
              {loading ? 'Registrando...' : 'Registrar Cliente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Customer Dialog */}
      <Dialog open={showEditCustomerDialog} onOpenChange={setShowEditCustomerDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Datos del Cliente</DialogTitle>
            <DialogDescription>
              Actualiza el nombre, email o fecha de cumpleaños del cliente
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Nombre</Label>
              <Input
                value={editCustomerData.name}
                onChange={(e) => setEditCustomerData({ ...editCustomerData, name: e.target.value })}
                placeholder="Nombre del cliente"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={editCustomerData.email}
                onChange={(e) => setEditCustomerData({ ...editCustomerData, email: e.target.value })}
                placeholder="email@ejemplo.com"
              />
            </div>
            <div>
              <Label>Cumpleaños (MM-DD)</Label>
              <Input
                value={editCustomerData.birthday}
                onChange={(e) => setEditCustomerData({ ...editCustomerData, birthday: e.target.value })}
                placeholder="Ej: 03-15 (15 de marzo)"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditCustomerDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleEditCustomer} 
              disabled={loading}
              className="bg-purple-500 hover:bg-purple-600"
            >
              {loading ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Points Confirmation Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <RotateCcw className="w-5 h-5" />
              Resetear Puntos
            </DialogTitle>
            <DialogDescription>
              Esta acción eliminará todos los puntos del cliente de forma permanente
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm font-medium text-red-800">⚠️ Advertencia</p>
              <p className="text-sm text-red-700 mt-1">
                Estás a punto de resetear todos los puntos de:
              </p>
              <p className="font-bold text-red-900 mt-2">
                {customerToReset?.customer_name || customerToReset?.customer_phone}
              </p>
              <p className="text-sm text-red-700 mt-1">
                Puntos actuales: <strong>{customerToReset?.total_points} pts</strong>
              </p>
              <p className="text-sm text-red-700 mt-2">
                Esta acción <strong>NO</strong> se puede deshacer.
              </p>
            </div>

            <div>
              <Label>Razón del Reset *</Label>
              <Input
                value={resetReason}
                onChange={(e) => setResetReason(e.target.value)}
                placeholder="Ej: Corrección de error, solicitud del cliente, etc."
              />
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowResetDialog(false);
                setCustomerToReset(null);
                setResetReason('');
              }}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleResetPoints} 
              disabled={loading || !resetReason.trim()}
              className="bg-red-500 hover:bg-red-600"
            >
              {loading ? 'Reseteando...' : 'Resetear Puntos'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoyaltyTab;
